import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import CONSTANTS from "../utils/constants";
import { useTranslation } from "react-i18next";

// Helper function to build hierarchical categories
const buildHierarchicalCategories = (
  categories,
  parentId = null,
  level = 0
) => {
  let hierarchicalList = [];
  const children = categories.filter((cat) => {
    if (parentId === null) return !cat.parent_id;
    return cat.parent_id === parentId;
  });
  children.sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
  for (const cat of children) {
    hierarchicalList.push({ ...cat, level });
    const childItems = buildHierarchicalCategories(
      categories,
      cat.id,
      level + 1
    );
    hierarchicalList = hierarchicalList.concat(childItems);
  }
  return hierarchicalList;
};

export default function AdminCategory() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);
  const [parentCategoryIds, setParentCategoryIds] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/categories`);
      setAllCategories(response.data);
      const parentIds = new Set(
        response.data.map((cat) => cat.parent_id).filter((id) => id !== null)
      );
      setParentCategoryIds(parentIds);

      const formattedData = buildHierarchicalCategories(response.data);
      setHierarchicalCategories(formattedData);
    } catch (err) {
      setError(t("adminCategory.loadError"));
      console.error("Kategoriler çekilirken hata:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = Cookies.get("user")
          ? JSON.parse(Cookies.get("user"))
          : null;
        if (!user?.id) {
          navigate("/admin");
          return;
        }

        const permissionsResponse = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          {
            params: { user_id: user.id },
          }
        );
        const permissions = permissionsResponse.data.permissions;
        setUserPermissions(permissions);

        if (!permissions.includes(11)) {
          setError(t("adminCategory.noPermission"));
          navigate("/admin/home");
          return;
        }

        await fetchCategories();
      } catch (err) {
        setError(t("adminCategory.dataLoadError"));
        console.error("Error fetching data:", err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate("/admin");
        }
      }
    };

    fetchData();
  }, [navigate, t]);

  const matchingCategories = useMemo(() => {
    if (!searchTerm.trim()) return hierarchicalCategories;

    const lowerSearch = searchTerm.toLowerCase();
    const matchingIds = new Set();

    allCategories.forEach((cat) => {
      if (cat.name_tm.toLowerCase().includes(lowerSearch)) {
        matchingIds.add(cat.id);
        let currentId = cat.parent_id;
        while (currentId) {
          matchingIds.add(currentId);
          const parent = allCategories.find((p) => p.id === currentId);
          currentId = parent ? parent.parent_id : null;
        }
      }
    });

    return hierarchicalCategories.filter((cat) => matchingIds.has(cat.id));
  }, [searchTerm, allCategories, hierarchicalCategories]);

  const visibleCategories = useMemo(() => {
    let baseCategories = matchingCategories;

    if (searchTerm.trim()) {
      return baseCategories;
    }

    if (collapsedCategories.size === 0) {
      return hierarchicalCategories;
    }

    const visible = [];
    let isHidden = false;
    let hiddenLevel = -1;

    for (const cat of baseCategories) {
      if (isHidden && cat.level > hiddenLevel) {
        continue;
      }

      isHidden = false;
      hiddenLevel = -1;

      visible.push(cat);

      if (collapsedCategories.has(cat.id)) {
        isHidden = true;
        hiddenLevel = cat.level;
      }
    }
    return visible;
  }, [hierarchicalCategories, collapsedCategories, matchingCategories, searchTerm]);

  const handleDelete = async (categoryId, categoryName) => {
    if (!userPermissions.includes(14)) {
      setError(t("adminCategory.noDeletePermission"));
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (
      window.confirm(
        t("adminCategory.confirmDelete", { name: categoryName })
      )
    ) {
      try {
        await axios.delete(`${CONSTANTS.baseRoute}/categories/${categoryId}`);
        setError(t("adminCategory.deleteSuccess"));
        await fetchCategories();
        setTimeout(() => setError(""), 3000);
      } catch (err) {
        setError(t("adminCategory.deleteError"));
        console.error("Kategori silinirken hata:", err);
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const handleToggleCollapse = (categoryId) => {
    setCollapsedCategories((prevCollapsed) => {
      const newCollapsed = new Set(prevCollapsed);
      if (newCollapsed.has(categoryId)) {
        newCollapsed.delete(categoryId);
      } else {
        newCollapsed.add(categoryId);
      }
      return newCollapsed;
    });
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="7" className="text-center">
            {t("common.loading")}
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan="7"
            className={`text-center text-${error.includes("üstünlikli") || error.includes("successfully") ? "success" : "danger"}`}
          >
            {error}
          </td>
        </tr>
      );
    }

    if (hierarchicalCategories.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="text-center">
            {t("adminCategory.noCategories")}
          </td>
        </tr>
      );
    }

    return visibleCategories.map((cat) => {
      const isParent = parentCategoryIds.has(cat.id);
      const isCollapsed = collapsedCategories.has(cat.id);

      return (
        <tr
          className="cursor-pointer"
          key={cat.id}
          onClick={() =>
            userPermissions.includes(13) &&
            navigate(`/admin/home/category/edit/${cat.id}`)
          }
        >
          <td>
            {isParent ? (
              <button
                className="btn btn-sm btn-link text-secondary p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleCollapse(cat.id);
                }}
                title={isCollapsed ? t("common.expand") : t("common.collapse")}
              >
                <i
                  className={`bi ${
                    isCollapsed ? "bi-chevron-down" : "bi-chevron-up"
                  }`}
                ></i>
              </button>
            ) : (
              <span style={{ display: "inline-block", width: "24px" }}></span>
            )}
          </td>
          <td className="text-start">
            {cat.image_path ? (
              <img
                src={`${CONSTANTS.baseRoute}${cat.image_path}`}
                alt={cat.name_tm}
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
            ) : (
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                <i className="bi bi-image text-muted"></i>
              </div>
            )}
          </td>
          <td className="text-start" style={{ paddingLeft: `${5 + cat.level * 25}px` }}>
            {cat.level > 0 && <span className="me-2 text-muted">└─</span>}
            {cat.name_tm}
          </td>
          <td>{cat.name_ru || "-"}</td>
          <td>{cat.order_number}</td>
          <td>
            {userPermissions.includes(13) && (
              <button
                className="btn btn-sm btn-outline-primary me-2"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/home/category/edit/${cat.id}`);
                }}
                title={t("common.edit")}
              >
                <i className="bi bi-pencil-fill"></i>
              </button>
            )}
            {userPermissions.includes(14) && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(cat.id, cat.name_tm);
                }}
                title={t("common.delete")}
              >
                <i className="bi bi-trash-fill"></i>
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="admin-main p-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h5 className="fw-bold mb-0">{t("adminCategory.title")}</h5>
        </div>

        {userPermissions.includes(12) && (
          <button
            className="btn btn-outline-primary d-flex gap-2 align-items-center"
            onClick={() => navigate("/admin/home/category/add")}
          >
            <i className="bi bi-plus-circle"></i>
            {t("adminCategory.addNew")}
          </button>
        )}
      </div>

      <div className="card bg-light p-3">
        <div className="input-group mb-3">
          <input
            className="form-control"
            placeholder={t("common.search")}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-outline-secondary" type="button">
            <i className="bi bi-search"></i>
          </button>
        </div>

        <div className="table-responsive admin-card">
          <table
            className="table table-hover mb-0"
            style={{ fontSize: "14px" }}
          >
            <thead className="admin-thead">
              <tr>
                <th style={{ width: "5%" }}></th>
                <th style={{ width: "10%" }}>{t("adminCategory.image")}</th>
                <th className="text-start">{t("adminCategory.nameTm")}</th>
                <th style={{ width: "15%" }}>{t("adminCategory.nameRu")}</th>
                <th style={{ width: "10%" }}>{t("adminCategory.order")}</th>
                <th style={{ width: "15%" }}></th>
              </tr>
            </thead>
            <tbody>{renderTableContent()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}